import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import LoginModal from "./LoginModal";
import LogoutButton from "./LogoutButton";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useNav } from "../contexts/NavContext"; // For hideNav toggle

import "./NavBarV2.css";

const NavBarV2 = ({ isLoggedIn, setIsLoggedIn }) => {
  const { hideNav } = useNav(); // Hide Nav Bar
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const location = useLocation();
  const isEntryOrRegisterPage = ["/entry", "/register"].includes(location.pathname);
  const navigate = useNavigate();
  const loginButtonRef = useRef(null);

  // Expose login modal trigger on Entry/Register only
  useEffect(() => {
    if (isEntryOrRegisterPage) {
      window.loginButtonRef = loginButtonRef;
      window.triggerLoginModal = (defaults = {}) => {
        setShowLoginModal(true);
        window.defaultLoginCredentials = defaults;
      };
    } else {
      window.loginButtonRef = null;
      window.triggerLoginModal = null;
    }
  }, [location.pathname, isEntryOrRegisterPage]);

  // Centralized fetchUser so we can call it from multiple places
  const fetchUser = useCallback(async () => {
    const accessToken = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setUser(null);
      return;
    }

    try {
      const config = {
        withCredentials: true,
        headers: {},
      };
      if (accessToken && accessToken !== "cookie") {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      const response = await axios.get(`${baseUrl}/users/account/${userId}`, config);
      setUser(response.data);
      setIsLoggedIn(true);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  }, [baseUrl, setIsLoggedIn]);

  // Initial fetch on mount
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Listen for auth changes (same-tab custom events + cross-tab storage)
  useEffect(() => {
    const onAuthLogin = () => {
      setIsLoggedIn(true);
      setShowLoginModal(false);
      fetchUser();
    };
    const onAuthLogout = () => {
      handleLogout();
    };
    const onStorage = (e) => {
      if (e.key === "token") {
        if (e.newValue) {
          onAuthLogin();
        } else {
          onAuthLogout();
        }
      }
    };

    window.addEventListener("auth:login", onAuthLogin);
    window.addEventListener("auth:logout", onAuthLogout);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("auth:login", onAuthLogin);
      window.removeEventListener("auth:logout", onAuthLogout);
      window.removeEventListener("storage", onStorage);
    };
  }, [fetchUser, setIsLoggedIn]);

  // Capture token/userId from URL (if you support that flow)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const userId = urlParams.get("userId");

    if (token && userId) {
      localStorage.setItem("token", token);
      localStorage.setItem("userId", userId);
      setIsLoggedIn(true);
      // Broadcast so this tab updates immediately
      window.dispatchEvent(new CustomEvent("auth:login", { detail: { token, userId } }));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [setIsLoggedIn]);

  if (hideNav) return null;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("role");
    setUser(null);
    setIsLoggedIn(false);
    navigate("/");
  };

  // If user logs in via this navbar modal, keep it consistent:
  const handleLogin = (userData) => {
    const token = userData?.token || "cookie";
    const userId = userData?.userId;

    if (token) localStorage.setItem("token", token);
    if (userId) localStorage.setItem("userId", String(userId));

    setIsLoggedIn(true);
    setShowLoginModal(false);

    // Broadcast so everyone updates
    window.dispatchEvent(new CustomEvent("auth:login", { detail: { token, userId } }));

    // Fetch full account for greeting/trial banner
    fetchUser();
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const getTrialDaysLeft = (trialStartDate) => {
    if (!trialStartDate) return 0;
    const start = new Date(trialStartDate);
    const now = new Date();
    const msInDay = 1000 * 60 * 60 * 24;
    const daysPassed = Math.floor((now - start) / msInDay);
    return Math.max(0, 14 - daysPassed);
    };

  return (
    <div>
      <nav className="navbarv2">
        <div className="navbar-brand">
          <Link
            to={user ? "/" : "/entry"}
            className="brand"
            onClick={() => window.scrollTo(0, 0)}
          >
            <div className="top-logo-bar">
              <div className="logo-wrapper">
                <div className="logo-squares">
                  <div className="square square1"></div>
                  <div className="square square2"></div>
                  <div className="square square3"></div>
                  <div className="square square4"></div>
                </div>
                <div className="blockcode-logo">
                  <span className="nav-logo-block">Block</span>
                  <span className="logo-divider">|</span>
                  <span className="logo-code">Code</span>
                </div>
              </div>
            </div>
          </Link>

          <div className="hamburger-wrapper">
            <button className="hamburger-button" onClick={toggleMenu}>
              ☰
            </button>
            {isMenuOpen && (
              <div className="navbar-dropdown">
                {user ? (
                  <>
                    <Link to="/problems" className="menu-item" onClick={() => setIsMenuOpen(false)}>
                      Problems
                    </Link>
                    <Link to="/learning-paths" className="menu-item" onClick={() => setIsMenuOpen(false)}>
                      Learning Paths
                    </Link>
                    <Link to="/create-problem" className="menu-item" onClick={() => setIsMenuOpen(false)}>
                      Import Problem
                    </Link>
                    <Link to="/membership" className="menu-item" onClick={() => setIsMenuOpen(false)}>
                      Membership
                    </Link>
                    <Link to="/account" className="menu-item" onClick={() => setIsMenuOpen(false)}>
                      Account
                    </Link>
                    <button
                      className="logout-button"
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <button
                    className="login-button"
                    onClick={() => {
                      setShowLoginModal(true);
                      setIsMenuOpen(false);
                    }}
                  >
                    Sign In / Register
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Show only when logged in and not on Entry */}
          {isLoggedIn && !isEntryOrRegisterPage && (
            <>
              <div className="menu-item-dropdown">
                <span
                  className={`menu-item ${
                    location.pathname === "/problems" ? "active-menu" : ""
                  }`}
                >
                  Problems ▾
                </span>
                <div className="dropdown-menu">
                  <Link to="/problems" className="dropdown-item">
                    All Problems
                  </Link>
                  <Link to="/learning-paths" className="dropdown-item">
                    Learning Paths
                  </Link>
                </div>
              </div>
              <Link
                to="/create-problem"
                className={`menu-item ${location.pathname === "/create-problem" ? "active-menu" : ""}`}
              >
                Import Problem
              </Link>
              <Link
                to="/membership"
                className={`menu-item ${location.pathname === "/membership" ? "active-menu" : ""}`}
              >
                Membership
              </Link>
            </>
          )}
        </div>

        <div className="navbar-actions">
          {user ? (
            <>
              <Link to="/account" className="navbar-user">
                Hello, {user.username}
              </Link>
              <LogoutButton onLogout={handleLogout} />
            </>
          ) : (
            <button
              ref={loginButtonRef}
              className="login-button"
              onClick={() => setShowLoginModal(true)}
            >
              Get Started
            </button>
          )}
        </div>

        {showLoginModal && (
          <LoginModal
            onLogin={handleLogin}
            onClose={() => setShowLoginModal(false)}
          />
        )}
      </nav>

      {user?.isTrialPremium && (
        <div className="trial-banner">
          🎉 You’re on a Premium trial — <strong>{getTrialDaysLeft(user.trialStartDate)}</strong> day
          {getTrialDaysLeft(user.trialStartDate) !== 1 && "s"} remaining! &nbsp;
          <Link to="/membership" className="trial-link">Upgrade Now</Link>
        </div>
      )}
    </div>
  );
};

export default NavBarV2;
